/**
 * WebGPU in-browser solve backend for the High-Lift workbench (FXC-9009).
 *
 * This is the SEAM, not the implementation. Drop Harry's WebGPU solver source into this
 * directory and implement `createWebGpuSolver()` against it. The workbench will call this
 * as a client-side solve backend ALONGSIDE the existing ones (cache → local RANS over SSH
 * to the V100 → cloud Flow360); it emits the SAME outputs those do, so nothing downstream
 * changes: a `FlowMesh` for `FlowVisLayer`, plus coefficients for the ADB grid / Tailor.
 *
 * MULTI-ELEMENT: `WebGpuSolveInput.elements` is the FULL deployed element collection from
 * `buildSweepInputs(config).elements` (N bodies — main + vane(s) + flaperon(s) + any added
 * foils), and `disks` is the blowing-source collection. The solver must mesh/solve the
 * multi-body domain (incl. the slots between elements), not a single body.
 *
 * See `thread/docs/webgpu-solver-integration.md` for the full fold-in brief.
 */
import type { FlowMesh, SurfaceCpElement } from "../foil/FlowVisLayer";
import { loadWasm } from "./wasm";

/** One deployed body: a closed contour in UI/chord coords (matches `buildSweepInputs().elements`). */
export interface WebGpuElement {
  name: string;
  contour: [number, number][];
}

/** An actuator-disk blowing source (matches the `disk` the RANS backends consume). */
export interface WebGpuDisk {
  center: [number, number];
  radius: number;
  thickness: number;
  axis: [number, number];
  loading: number;
}

/** Everything the solver needs for one operating point. Assemble from `buildSweepInputs(config)`. */
export interface WebGpuSolveInput {
  /** ALL deployed bodies (multi-element). `buildSweepInputs(config).elements`. */
  elements: WebGpuElement[];
  /** Blowing sources (0..N). Today `buildSweepInputs` emits one `disk`; pass `[]` if none. */
  disks: WebGpuDisk[];
  /** Freestream for the point. `buildSweepInputs(config).flow`. */
  flow: { mach: number; reynolds: number; temperature: number };
  /** Angle of attack (deg) — owned by the surface (`alphaDeg`), not the config. */
  alphaDeg: number;
  /** Solver iteration cap (optional). */
  maxSteps?: number;
}

/** Live progress for the convergence overlay (mirror of the local-solve progress poll). */
export interface WebGpuSolveProgress {
  percentage: number;
  stage?: "meshing" | "solving" | "extracting";
  CL?: number;
  /** Downsampled CL trajectory so the workbench can replay the convergence sweep. */
  clHistory?: number[];
}

/** Result the workbench consumes — the field the viewer renders + the coefficients. */
export interface WebGpuSolveResult {
  /** Center-span field for `FlowVisLayer` (UI coords; same shape as `flowfield_extract`). */
  flow: FlowMesh;
  CL: number;
  CD: number;
  CM?: number;
  L_over_D?: number;
  steps?: number;
  clHistory?: number[];
  /** Native convergence diagnostics carried across the WASM boundary. */
  diagnostics: {
    evidenceStatus: "solver-native";
    solver: "rustfoil-wasm-webgpu-2d-rans";
    converged: boolean | null;
    residual?: number;
    saResidual?: number;
    iterations?: number;
    cellCount?: number;
    adapter?: string;
  };
}

/**
 * A persisted warm-start solve session (FXC-9009 P3). Wraps a `WebGpuRansSession` that
 * holds the wgpu device + the last converged (mesh, field), so a control-point drag
 * re-solves cheaply via `resolve` (warm — interpolate the stored field onto the
 * re-meshed geometry, fewer iters) instead of `solve` (cold — full iters from
 * freestream). Both flatten the input exactly like the one-shot `solve` and lift the
 * GPU field into the same `WebGpuSolveResult` shape.
 */
export interface WebGpuSession {
  /** COLD solve: full iters from freestream, stores the converged field. */
  solve(input: WebGpuSolveInput, onProgress?: (p: WebGpuSolveProgress) => void): Promise<WebGpuSolveResult>;
  /** WARM re-solve: interpolate the stored field onto the new mesh + run fewer iters. */
  resolve(input: WebGpuSolveInput, onProgress?: (p: WebGpuSolveProgress) => void): Promise<WebGpuSolveResult>;
  /** Release the persisted wgpu device + stored field. Idempotent. */
  free(): void;
}

export interface WebGpuSolver {
  /** True when a WebGPU adapter is available (gate the backend on this). */
  available(): boolean | Promise<boolean>;
  /** Solve one operating point; call `onProgress` to drive the convergence overlay. */
  solve(input: WebGpuSolveInput, onProgress?: (p: WebGpuSolveProgress) => void): Promise<WebGpuSolveResult>;
  /**
   * Create a persisted warm-start session for live drag-to-resolve. The first `solve`
   * is cold (stores the field); subsequent `resolve`s warm-start from it. `free()` when
   * the backend switches or the surface unmounts.
   */
  createSession(): Promise<WebGpuSession>;
}

/**
 * The serde-serialized result of `analyze_rans_gpu_multi` (the wasm boundary returns
 * `Promise<any>`, so we pin the shape here). `points`/`vel`/`mach`/`tris`/`machRange`
 * are ALREADY a FlowMesh (per-node flat arrays, chord/FOIL_VIEW frame, clipped) — we
 * lift them straight into `FlowMesh`.
 */
interface GpuFieldResult {
  points: number[];
  vel: number[];
  mach: number[];
  tris: number[];
  machRange: [number, number];
  cl: number;
  cd: number;
  cp_x?: number[];
  cp?: number[];
  iters?: number;
  residual?: number;
  sa_residual?: number;
  converged?: boolean;
  n_cells?: number;
  adapter?: string;
  success: boolean;
  error?: string | null;
}

/** Default cold-solve iteration cap (full convergence) when `maxSteps` is omitted. */
const COLD_MAX_ITERS = 6000;
/** Warm-start iteration budget for a `resolve` (interpolated seed → fewer iters). */
const WARM_ITERS = 1500;

/** The flattened wasm wire for one operating point (shared by the one-shot + session paths). */
interface FlattenedInput {
  contoursFlat: Float64Array;
  lengths: Uint32Array;
  disksFlat: Float64Array;
  nDisks: number;
}

/**
 * Flatten a `WebGpuSolveInput` into the wasm wire: all element `[x,y]` pairs concatenated
 * with the per-element point counts, plus the actuator-disk sources as 7 floats each
 * (center.x, center.y, radius, thickness, axis.x, axis.y, loading). This is the EXACT
 * flattening the one-shot `solve` used, lifted out so the session reuses it verbatim.
 */
function flattenInput(input: WebGpuSolveInput): FlattenedInput {
  const lengths = new Uint32Array(input.elements.length);
  let total = 0;
  for (let i = 0; i < input.elements.length; i++) {
    const n = input.elements[i]!.contour.length;
    lengths[i] = n;
    total += n;
  }
  const contoursFlat = new Float64Array(total * 2);
  let w = 0;
  for (const el of input.elements) {
    for (const [x, y] of el.contour) {
      contoursFlat[w++] = x;
      contoursFlat[w++] = y;
    }
  }

  const disksFlat = new Float64Array(input.disks.length * 7);
  for (let i = 0; i < input.disks.length; i++) {
    const d = input.disks[i]!;
    const o = i * 7;
    disksFlat[o] = d.center[0];
    disksFlat[o + 1] = d.center[1];
    disksFlat[o + 2] = d.radius;
    disksFlat[o + 3] = d.thickness;
    disksFlat[o + 4] = d.axis[0];
    disksFlat[o + 5] = d.axis[1];
    disksFlat[o + 6] = d.loading;
  }

  return { contoursFlat, lengths, disksFlat, nDisks: input.disks.length };
}

/** Lift the serde-serialized GPU field into the `WebGpuSolveResult` the workbench consumes. */
function liftResult(r: GpuFieldResult): WebGpuSolveResult {
  if (!r.success) throw new Error(r.error ?? "webgpu solve failed");

  const finiteDiagnostic = (value: unknown): number | undefined => (
    typeof value === "number" && Number.isFinite(value) ? value : undefined
  );
  const residual = finiteDiagnostic(r.residual);
  const saResidual = finiteDiagnostic(r.sa_residual);
  const iterations = finiteDiagnostic(r.iters);
  const cellCount = finiteDiagnostic(r.n_cells);

  const flow: FlowMesh = {
    points: Array.from(r.points),
    tris: Array.from(r.tris),
    vel: Array.from(r.vel),
    mach: Array.from(r.mach),
    machRange: r.machRange as [number, number],
    surfaceCp: surfaceCpFromTrace(r.cp_x, r.cp),
  };

  return {
    flow,
    CL: r.cl,
    CD: r.cd,
    L_over_D: r.cd ? r.cl / r.cd : undefined,
    steps: r.iters,
    clHistory: [r.cl],
    diagnostics: {
      evidenceStatus: "solver-native",
      solver: "rustfoil-wasm-webgpu-2d-rans",
      converged: typeof r.converged === "boolean" ? r.converged : null,
      ...(residual === undefined ? {} : { residual }),
      ...(saResidual === undefined ? {} : { saResidual }),
      ...(iterations === undefined ? {} : { iterations }),
      ...(cellCount === undefined ? {} : { cellCount }),
      ...(typeof r.adapter === "string" && r.adapter ? { adapter: r.adapter } : {}),
    },
  };
}

/**
 * Convert the single-body Selig-ordered pressure trace emitted by rustfoil-wasm
 * (upper TE -> LE -> lower TE) into the upper/lower contract used by CpPlot.
 * Multi-body boundary ownership is not present on the wasm wire yet, so this seam
 * deliberately exposes one honest "section" trace instead of guessing element IDs.
 */
export function surfaceCpFromTrace(
  rawX: ArrayLike<number> | undefined,
  rawCp: ArrayLike<number> | undefined,
): SurfaceCpElement[] | undefined {
  if (!rawX || !rawCp) return undefined;
  const count = Math.min(rawX.length, rawCp.length);
  const points: [number, number][] = [];
  for (let index = 0; index < count; index += 1) {
    const x = Number(rawX[index]);
    const cp = Number(rawCp[index]);
    if (Number.isFinite(x) && Number.isFinite(cp)) points.push([x, cp]);
  }
  if (points.length < 2) return undefined;

  let leadingEdgeIndex = 0;
  for (let index = 1; index < points.length; index += 1) {
    if (points[index]![0] < points[leadingEdgeIndex]![0]) leadingEdgeIndex = index;
  }

  const ascendingX = (a: [number, number], b: [number, number]) => a[0] - b[0];
  const upper = leadingEdgeIndex > 0
    ? points.slice(0, leadingEdgeIndex + 1).reverse().sort(ascendingX)
    : points.slice().sort(ascendingX);
  const lower = leadingEdgeIndex < points.length - 1
    ? points.slice(leadingEdgeIndex).sort(ascendingX)
    : [];

  return [{ element: "section", upper, lower }];
}

/**
 * The client-side WebGPU 2D RANS solve backend (FXC-9009 P4/P3). Lazily loads the vendored
 * `rustfoil-wasm` package. The one-shot `solve` calls `analyze_rans_gpu_multi`; `createSession`
 * wraps the stateful `WebGpuRansSession` (warm-start) for live drag-to-resolve. All paths emit
 * the SAME `FlowMesh` + coefficients the RANS/V100 backends do.
 *
 * Disks (blowing) are accepted on the input but IGNORED by the solver this phase (incremental +
 * blowing are later phases): they are flattened onto the wire but the solver path is unchanged.
 */
export function createWebGpuSolver(): WebGpuSolver {
  return {
    async available(): Promise<boolean> {
      if (!("gpu" in navigator)) return false;
      try {
        const adapter = await navigator.gpu.requestAdapter();
        return !!adapter;
      } catch {
        return false;
      }
    },

    async solve(
      input: WebGpuSolveInput,
      onProgress?: (p: WebGpuSolveProgress) => void,
    ): Promise<WebGpuSolveResult> {
      const { contoursFlat, lengths, disksFlat, nDisks } = flattenInput(input);

      onProgress?.({ percentage: 5, stage: "meshing" });
      const m = await loadWasm();
      onProgress?.({ percentage: 20, stage: "solving" });

      const r = (await m.analyze_rans_gpu_multi(
        contoursFlat,
        lengths,
        input.flow.mach,
        input.flow.reynolds,
        input.alphaDeg,
        input.maxSteps ?? COLD_MAX_ITERS,
        disksFlat,
        nDisks,
      )) as GpuFieldResult;

      const result = liftResult(r);
      onProgress?.({ percentage: 100, stage: "extracting", CL: result.CL, clHistory: result.clHistory });
      return result;
    },

    async createSession(): Promise<WebGpuSession> {
      const m = await loadWasm();
      // The wasm async constructor: wasm-bindgen surfaces it as a `new`-able class whose
      // construction may resolve a Promise (it acquires + persists the wgpu device). Await
      // the instance so `solve`/`resolve` run against a ready session. A null instance (no
      // adapter) surfaces as a thrown error on the first solve, matching the one-shot path.
      const session = (await Promise.resolve(
        new m.WebGpuRansSession(),
      )) as InstanceType<typeof m.WebGpuRansSession>;
      if (!session) throw new Error("no WebGPU adapter available for session");

      return {
        async solve(
          input: WebGpuSolveInput,
          onProgress?: (p: WebGpuSolveProgress) => void,
        ): Promise<WebGpuSolveResult> {
          const { contoursFlat, lengths, disksFlat, nDisks } = flattenInput(input);
          onProgress?.({ percentage: 5, stage: "meshing" });
          onProgress?.({ percentage: 20, stage: "solving" });
          const r = (await session.solve(
            contoursFlat,
            lengths,
            input.flow.mach,
            input.flow.reynolds,
            input.alphaDeg,
            input.maxSteps ?? COLD_MAX_ITERS,
            disksFlat,
            nDisks,
          )) as GpuFieldResult;
          const result = liftResult(r);
          onProgress?.({ percentage: 100, stage: "extracting", CL: result.CL, clHistory: result.clHistory });
          return result;
        },

        async resolve(
          input: WebGpuSolveInput,
          onProgress?: (p: WebGpuSolveProgress) => void,
        ): Promise<WebGpuSolveResult> {
          const { contoursFlat, lengths, disksFlat, nDisks } = flattenInput(input);
          // Warm re-solve: the session interpolates its stored field onto the re-meshed
          // geometry and runs the smaller `WARM_ITERS` budget — so a drag updates the field
          // live without a cold restart. Mesh stage is near-instant (re-mesh of a tweak).
          onProgress?.({ percentage: 10, stage: "meshing" });
          onProgress?.({ percentage: 30, stage: "solving" });
          const r = (await session.resolve(
            contoursFlat,
            lengths,
            input.flow.mach,
            input.flow.reynolds,
            input.alphaDeg,
            WARM_ITERS,
            disksFlat,
            nDisks,
          )) as GpuFieldResult;
          const result = liftResult(r);
          onProgress?.({ percentage: 100, stage: "extracting", CL: result.CL, clHistory: result.clHistory });
          return result;
        },

        free(): void {
          try {
            session.free();
          } catch {
            // already freed / disposed — idempotent.
          }
        },
      };
    },
  };
}
